import { useEffect, useState } from 'react';
import {
  ASSESSMENT_STATUS_HINTS,
  nextAssessmentStatuses,
  type AssessmentEnquiryStatus,
} from '@lighthouse/shared';
import { adminApi, ensureArray } from '../services/api';

interface AssessmentEnquiry {
  id: string;
  publicAccessToken: string;
  assessmentType: string;
  fullName: string;
  email: string;
  phone: string | null;
  ageCategory: string | null;
  reason: string | null;
  preferredFormat: string | null;
  preferredContact: string | null;
  status: AssessmentEnquiryStatus;
  adminNotes: string | null;
  assignedToUserId: string | null;
  consultationFeeNgn: number | null;
  assessmentQuoteNgn: number | null;
  quoteSummary: string | null;
  consultationAt: string | null;
  assessmentAt: string | null;
  quoteIssuedAt: string | null;
  clientApprovedAt: string | null;
  consultationPaidAt: string | null;
  assessmentPaidAt: string | null;
  createdAt: string;
  updatedAt: string;
}

const WEB_BASE = (
  (import.meta.env.VITE_WEB_URL as string | undefined) || 'https://mylighthouse.ng'
).replace(/\/$/, '');

const STATUS_LABELS: Record<AssessmentEnquiryStatus, string> = {
  request_received: 'Request received',
  under_review: 'Under review',
  consultation_scheduled: 'Consultation scheduled',
  assessment_proposed: 'Assessment proposed',
  awaiting_approval: 'Awaiting approval',
  assessment_scheduled: 'Assessment scheduled',
  in_progress: 'In progress',
  feedback_pending: 'Feedback pending',
  completed: 'Completed',
  cancelled: 'Cancelled',
};

function formatWhen(iso: string | null): string {
  if (!iso) return '—';
  try {
    return new Date(iso).toLocaleString();
  } catch {
    return iso;
  }
}

function formatNaira(value: number | null): string {
  if (value == null) return '—';
  return `₦${value.toLocaleString('en-NG')}`;
}

function toDatetimeLocalValue(iso: string | null): string {
  if (!iso) return '';
  const date = new Date(iso);
  if (Number.isNaN(date.getTime())) return '';
  const pad = (n: number) => String(n).padStart(2, '0');
  return `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())}T${pad(date.getHours())}:${pad(date.getMinutes())}`;
}

function fromDatetimeLocalValue(value: string): string | null {
  if (!value.trim()) return null;
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return null;
  return date.toISOString();
}

export function AssessmentsPage() {
  const [enquiries, setEnquiries] = useState<AssessmentEnquiry[]>([]);
  const [loading, setLoading] = useState(true);
  const [statusFilter, setStatusFilter] = useState<AssessmentEnquiryStatus | ''>('');
  const [selectedId, setSelectedId] = useState<string | null>(null);
  const [notesDraft, setNotesDraft] = useState('');
  const [statusDraft, setStatusDraft] = useState<AssessmentEnquiryStatus>('request_received');
  const [consultationFeeDraft, setConsultationFeeDraft] = useState('');
  const [quoteDraft, setQuoteDraft] = useState('');
  const [quoteSummaryDraft, setQuoteSummaryDraft] = useState('');
  const [consultationAtDraft, setConsultationAtDraft] = useState('');
  const [assessmentAtDraft, setAssessmentAtDraft] = useState('');
  const [consultationPaid, setConsultationPaid] = useState(false);
  const [assessmentPaid, setAssessmentPaid] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');

  const selected = enquiries.find((item) => item.id === selectedId) ?? null;
  const allowedStatuses = selected
    ? nextAssessmentStatuses(selected.status)
    : (['request_received'] as AssessmentEnquiryStatus[]);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    setError('');
    const query = statusFilter ? `?status=${statusFilter}` : '';
    adminApi<AssessmentEnquiry[]>(`/admin/assessment-enquiries${query}`)
      .then((data) => {
        if (cancelled) return;
        const list = ensureArray<AssessmentEnquiry>(data);
        setEnquiries(list);
        setSelectedId((current) =>
          current && !list.some((item) => item.id === current) ? null : current
        );
      })
      .catch((err: unknown) => {
        if (cancelled) return;
        setEnquiries([]);
        setError(err instanceof Error ? err.message : 'Failed to load enquiries');
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [statusFilter]);

  useEffect(() => {
    if (!selected) {
      setNotesDraft('');
      setStatusDraft('request_received');
      setConsultationFeeDraft('');
      setQuoteDraft('');
      setQuoteSummaryDraft('');
      setConsultationAtDraft('');
      setAssessmentAtDraft('');
      setConsultationPaid(false);
      setAssessmentPaid(false);
      return;
    }
    setNotesDraft(selected.adminNotes ?? '');
    setStatusDraft(selected.status);
    setConsultationFeeDraft(
      selected.consultationFeeNgn != null ? String(selected.consultationFeeNgn) : ''
    );
    setQuoteDraft(selected.assessmentQuoteNgn != null ? String(selected.assessmentQuoteNgn) : '');
    setQuoteSummaryDraft(selected.quoteSummary ?? '');
    setConsultationAtDraft(toDatetimeLocalValue(selected.consultationAt));
    setAssessmentAtDraft(toDatetimeLocalValue(selected.assessmentAt));
    setConsultationPaid(Boolean(selected.consultationPaidAt));
    setAssessmentPaid(Boolean(selected.assessmentPaidAt));
  }, [selected]);

  const save = async (extras?: { sendPaymentLinkEmail?: boolean }) => {
    if (!selected) return;
    setSaving(true);
    setError('');
    try {
      const consultationFeeNgn = consultationFeeDraft.trim()
        ? Number.parseInt(consultationFeeDraft.trim(), 10)
        : null;
      const assessmentQuoteNgn = quoteDraft.trim() ? Number.parseInt(quoteDraft.trim(), 10) : null;
      if (consultationFeeDraft.trim() && Number.isNaN(consultationFeeNgn)) {
        throw new Error('Consultation fee must be a whole naira amount');
      }
      if (quoteDraft.trim() && Number.isNaN(assessmentQuoteNgn)) {
        throw new Error('Assessment quote must be a whole naira amount');
      }
      const updated = await adminApi<AssessmentEnquiry>(
        `/admin/assessment-enquiries/${selected.id}`,
        {
          method: 'PATCH',
          body: JSON.stringify({
            status: statusDraft,
            adminNotes: notesDraft.trim() ? notesDraft.trim() : null,
            consultationFeeNgn,
            assessmentQuoteNgn,
            quoteSummary: quoteSummaryDraft.trim() ? quoteSummaryDraft.trim() : null,
            consultationAt: fromDatetimeLocalValue(consultationAtDraft),
            assessmentAt: fromDatetimeLocalValue(assessmentAtDraft),
            consultationPaid,
            assessmentPaid,
            ...(extras?.sendPaymentLinkEmail ? { sendPaymentLinkEmail: true } : {}),
          }),
        }
      );
      setEnquiries((prev) => prev.map((item) => (item.id === updated.id ? updated : item)));
    } catch (err: unknown) {
      setError(err instanceof Error ? err.message : 'Failed to update enquiry');
    } finally {
      setSaving(false);
    }
  };

  const resendPaymentEmail = async () => {
    await save({ sendPaymentLinkEmail: true });
  };

  return (
    <div className="p-6">
      <div className="mb-6 flex flex-wrap items-end justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Assessment Management</h1>
          <p className="mt-0.5 text-sm text-gray-500">
            Enquiry-first workflow with consultation fees, quotations, and payment markers.
          </p>
        </div>
        <label className="text-sm text-gray-600">
          Status
          <select
            className="ml-2 rounded-lg border border-gray-300 px-3 py-2 text-sm"
            value={statusFilter}
            onChange={(e) => setStatusFilter(e.target.value as AssessmentEnquiryStatus | '')}
          >
            <option value="">All</option>
            {(Object.keys(STATUS_LABELS) as AssessmentEnquiryStatus[]).map((status) => (
              <option key={status} value={status}>
                {STATUS_LABELS[status]}
              </option>
            ))}
          </select>
        </label>
      </div>

      {error ? (
        <p className="mb-4 rounded-lg border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-700">
          {error}
        </p>
      ) : null}

      {loading ? (
        <p className="text-sm text-gray-500">Loading…</p>
      ) : (
        <div className="grid gap-6 lg:grid-cols-[1.15fr_0.85fr]">
          <div className="overflow-hidden rounded-xl border border-gray-200 bg-white shadow-sm">
            <table className="min-w-full divide-y divide-gray-200 text-sm">
              <thead className="bg-gray-50 text-left text-xs font-semibold uppercase tracking-wide text-gray-500">
                <tr>
                  <th className="px-4 py-3">Requester</th>
                  <th className="px-4 py-3">Fees</th>
                  <th className="px-4 py-3">Status</th>
                  <th className="px-4 py-3">Received</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100">
                {enquiries.length === 0 ? (
                  <tr>
                    <td colSpan={4} className="px-4 py-8 text-center text-gray-500">
                      No assessment enquiries yet.
                    </td>
                  </tr>
                ) : (
                  enquiries.map((enquiry) => (
                    <tr
                      key={enquiry.id}
                      className={`cursor-pointer hover:bg-gray-50 ${
                        selectedId === enquiry.id ? 'bg-emerald-50/60' : ''
                      }`}
                      onClick={() => setSelectedId(enquiry.id)}
                    >
                      <td className="px-4 py-3">
                        <div className="font-medium text-gray-900">{enquiry.fullName}</div>
                        <div className="text-xs text-gray-500">{enquiry.email}</div>
                      </td>
                      <td className="px-4 py-3 text-xs text-gray-600">
                        <div>Consult {formatNaira(enquiry.consultationFeeNgn)}</div>
                        <div>Quote {formatNaira(enquiry.assessmentQuoteNgn)}</div>
                      </td>
                      <td className="px-4 py-3 text-gray-700">
                        {STATUS_LABELS[enquiry.status] ?? enquiry.status}
                      </td>
                      <td className="px-4 py-3 text-gray-500">{formatWhen(enquiry.createdAt)}</td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>

          <div className="rounded-xl border border-gray-200 bg-white p-5 shadow-sm">
            {selected ? (
              <div className="space-y-4">
                <div>
                  <h2 className="text-lg font-semibold text-gray-900">{selected.fullName}</h2>
                  <p className="text-sm text-gray-500">{selected.email}</p>
                  {selected.phone ? (
                    <p className="text-sm text-gray-500">{selected.phone}</p>
                  ) : null}
                  <p className="mt-2 rounded-lg bg-emerald-50 px-3 py-2 text-xs text-emerald-900">
                    {ASSESSMENT_STATUS_HINTS[selected.status]}
                  </p>
                </div>
                <dl className="grid gap-2 text-sm">
                  <div>
                    <dt className="text-xs font-semibold uppercase text-gray-400">Format</dt>
                    <dd className="text-gray-700">{selected.preferredFormat ?? '—'}</dd>
                  </div>
                  <div>
                    <dt className="text-xs font-semibold uppercase text-gray-400">Reason</dt>
                    <dd className="whitespace-pre-wrap text-gray-700">{selected.reason ?? '—'}</dd>
                  </div>
                </dl>

                <label className="block text-sm text-gray-700">
                  Status
                  <select
                    className="mt-1 w-full rounded-lg border border-gray-300 px-3 py-2"
                    value={statusDraft}
                    onChange={(e) => setStatusDraft(e.target.value as AssessmentEnquiryStatus)}
                  >
                    {allowedStatuses.map((status) => (
                      <option key={status} value={status}>
                        {STATUS_LABELS[status]}
                      </option>
                    ))}
                  </select>
                </label>

                <div className="grid gap-3 sm:grid-cols-2">
                  <label className="block text-sm text-gray-700">
                    Consultation fee (₦)
                    <input
                      type="number"
                      min={100}
                      step={100}
                      className="mt-1 w-full rounded-lg border border-gray-300 px-3 py-2"
                      value={consultationFeeDraft}
                      onChange={(e) => setConsultationFeeDraft(e.target.value)}
                    />
                  </label>
                  <label className="block text-sm text-gray-700">
                    Assessment quote (₦)
                    <input
                      type="number"
                      min={100}
                      step={100}
                      className="mt-1 w-full rounded-lg border border-gray-300 px-3 py-2"
                      value={quoteDraft}
                      onChange={(e) => setQuoteDraft(e.target.value)}
                    />
                  </label>
                  <label className="block text-sm text-gray-700">
                    Consultation time
                    <input
                      type="datetime-local"
                      className="mt-1 w-full rounded-lg border border-gray-300 px-3 py-2"
                      value={consultationAtDraft}
                      onChange={(e) => setConsultationAtDraft(e.target.value)}
                    />
                  </label>
                  <label className="block text-sm text-gray-700">
                    Assessment time
                    <input
                      type="datetime-local"
                      className="mt-1 w-full rounded-lg border border-gray-300 px-3 py-2"
                      value={assessmentAtDraft}
                      onChange={(e) => setAssessmentAtDraft(e.target.value)}
                    />
                  </label>
                </div>

                <label className="block text-sm text-gray-700">
                  Quote summary
                  <textarea
                    className="mt-1 min-h-[80px] w-full rounded-lg border border-gray-300 px-3 py-2"
                    value={quoteSummaryDraft}
                    onChange={(e) => setQuoteSummaryDraft(e.target.value)}
                    maxLength={4000}
                    placeholder="What the quotation covers"
                  />
                </label>

                <div className="rounded-lg border border-emerald-100 bg-emerald-50/70 p-3 text-sm">
                  <p className="font-medium text-emerald-950">Client payment link</p>
                  <p className="mt-1 break-all text-xs text-emerald-900">
                    {`${WEB_BASE}/assessments/pay/${selected.publicAccessToken}`}
                  </p>
                  <div className="mt-2 flex flex-wrap gap-2">
                    <button
                      type="button"
                      className="rounded-md bg-white px-3 py-1.5 text-xs font-semibold text-emerald-900 ring-1 ring-emerald-200"
                      onClick={() => {
                        void navigator.clipboard.writeText(
                          `${WEB_BASE}/assessments/pay/${selected.publicAccessToken}`
                        );
                      }}
                    >
                      Copy payment link
                    </button>
                    <button
                      type="button"
                      className="rounded-md bg-emerald-900 px-3 py-1.5 text-xs font-semibold text-white disabled:opacity-50"
                      disabled={saving}
                      onClick={() => void resendPaymentEmail()}
                    >
                      Email payment link
                    </button>
                  </div>
                  <p className="mt-2 text-xs text-emerald-800">
                    Setting or changing a fee/quote emails the client automatically. Paystack
                    confirms payments; use the markers below only for offline/manual settlement.
                  </p>
                </div>

                <div className="flex flex-wrap gap-4 text-sm text-gray-700">
                  <label className="inline-flex items-center gap-2">
                    <input
                      type="checkbox"
                      checked={consultationPaid}
                      onChange={(e) => setConsultationPaid(e.target.checked)}
                    />
                    Consultation marked paid (manual)
                  </label>
                  <label className="inline-flex items-center gap-2">
                    <input
                      type="checkbox"
                      checked={assessmentPaid}
                      onChange={(e) => setAssessmentPaid(e.target.checked)}
                    />
                    Assessment marked paid (manual)
                  </label>
                </div>

                <p className="text-xs text-gray-500">
                  Quote issued: {formatWhen(selected.quoteIssuedAt)} · Client approved:{' '}
                  {formatWhen(selected.clientApprovedAt)} · Consult paid:{' '}
                  {formatWhen(selected.consultationPaidAt)} · Assessment paid:{' '}
                  {formatWhen(selected.assessmentPaidAt)}
                </p>

                <label className="block text-sm text-gray-700">
                  Admin notes
                  <textarea
                    className="mt-1 min-h-[100px] w-full rounded-lg border border-gray-300 px-3 py-2"
                    value={notesDraft}
                    onChange={(e) => setNotesDraft(e.target.value)}
                    maxLength={5000}
                  />
                </label>
                <button
                  type="button"
                  className="rounded-lg bg-gray-900 px-4 py-2 text-sm font-medium text-white disabled:opacity-50"
                  onClick={() => void save()}
                  disabled={saving}
                >
                  {saving ? 'Saving…' : 'Save updates'}
                </button>
              </div>
            ) : (
              <p className="text-sm text-gray-500">
                Select an enquiry to review fees, quotations, and status.
              </p>
            )}
          </div>
        </div>
      )}
    </div>
  );
}
